import React, { useState, useEffect, useRef } from "react";
import ReportsHelpers from "../../utils/ReportsHelpers";
import withLazyLoad from "../../utils/withLazyLoad";
import {
  utcDayStart,
  utcDayEnd,
  utcDayInputValue,
} from "../../utils/utcDayBoundary";
const StepReport = withLazyLoad(() => import("../steps/StepReport"));

const SUBMISSIONS_TIMEFRAME_OPTIONS = new Set([
  "Last 24 Hours",
  "Last 7 Days",
  "Last 30 Days",
  "Last 90 Days",
  "Last 365 Days",
]);

const variantLabel = (variant) => {
  if (!variant) return "";
  const name = variant.name ? ` (${variant.name})` : "";
  return `Variant ${String(variant.variation).toUpperCase()}${name}`;
};

const findVariantById = (variants, variantId) => {
  if (!variantId) return null;
  return (
    Object.values(variants).find((v) => String(v.id) === String(variantId)) ||
    null
  );
};

const VariantReport = (props) => {
  const cta = props.cta;
  const getFallbackTimeframe = () =>
    props.timeframeTypes.filter((option) => option.selected == true)[0].value;
  const resolveSelectedTimeframe = () => {
    if (
      props.selectedTimeframe &&
      props.timeframeTypes.some((t) => t.value == props.selectedTimeframe)
    ) {
      return props.selectedTimeframe;
    }
    return getFallbackTimeframe();
  };
  const [tab, setTab] = useState("conversions");
  const [timeframeType, setTimeframeType] = useState(
    resolveSelectedTimeframe()
  );
  const [rangeStartValue, setRangeStartValue] = useState(
    props.selectedRangeStart ||
      props.timeframeTypes.filter((t) => t.value == "Last 24 Hours")[0]
        .range_start
  );
  const [rangeEndValue, setRangeEndValue] = useState(
    props.selectedRangeEnd ||
      props.timeframeTypes.filter((t) => t.value == "Last 24 Hours")[0]
        .range_end
  );
  const [variantId, setVariantId] = useState(props.variantId);
  const [compareVariantId, setCompareVariantId] = useState("");
  const variant = findVariantById(props.variants, variantId);
  const compareVariant = findVariantById(props.variants, compareVariantId);
  const isComparing = Boolean(variant && compareVariant);
  const selectableVariants = props.cta.variants || [];
  const compareOptions = selectableVariants.filter(
    (option) => String(option.id) !== String(variantId)
  );
  const wistia = useRef(null);
  const gateReports = props.gateReports;

  let group_by = ReportsHelpers.getGroupBy(timeframeType);
  let range_start = props.timeframeTypes.filter(
    (t) => t.value == timeframeType
  )[0].range_start;
  let range_end = props.timeframeTypes.filter(
    (t) => t.value == timeframeType
  )[0].range_end;

  if (timeframeType == "Date Range") {
    range_start = rangeStartValue;
    range_end = rangeEndValue;
    group_by = ReportsHelpers.getGroupBy(null, range_start, range_end);
  }

  const buildSubmissionsPath = ({ stepId } = {}) => {
    if (!variant) return "#";

    const params = new URLSearchParams({
      contactable_id: String(cta.id),
      variant_id: String(variant.id),
    });

    if (stepId) params.set("step_id", String(stepId));

    if (timeframeType !== "All Time" && range_start && range_end) {
      params.set("range_start", range_start);
      params.set("range_end", range_end);
    }

    if (SUBMISSIONS_TIMEFRAME_OPTIONS.has(timeframeType)) {
      params.set("timeframe", timeframeType);
    }

    return `/websites/${
      cta.website_id
    }/events/submissions?${params.toString()}`;
  };

  const variantSubmissionsPath = buildSubmissionsPath();
  const timeframeParamMap = {
    "All Time": "all_time",
    Today: "today",
    "This Week": "this_week",
    "This Month": "this_month",
    "This Quarter": "this_quarter",
    "This Year": "this_year",
    "Last 24 Hours": "last_24_hours",
    "Last 7 Days": "last_7_days",
    "Last 30 Days": "last_30_days",
    "Last 90 Days": "last_90_days",
    "Last 365 Days": "last_365_days",
    "Date Range": "custom",
  };
  const exportTimeframeParam = timeframeParamMap[timeframeType] || "all_time";
  const resolveFunnelChatTimeframe = () => {
    const mapped = timeframeParamMap[timeframeType];
    if (mapped && mapped !== "custom") {
      return { timeframe: mapped, startDate: null, endDate: null };
    }

    // Date Range and labels without a Reports::Timeframe enum (e.g. Yesterday)
    // are sent as custom with concrete bounds so the chat snapshot matches the report.
    if (timeframeType === "Date Range") {
      return {
        timeframe: "custom",
        startDate: utcDayInputValue(rangeStartValue) || null,
        endDate: utcDayInputValue(rangeEndValue) || null,
      };
    }

    const option = props.timeframeTypes.find((t) => t.value === timeframeType);
    const start =
      option?.range_start && option.range_start !== "daterange"
        ? utcDayInputValue(option.range_start)
        : "";
    const end = option?.range_end ? utcDayInputValue(option.range_end) : "";
    return {
      timeframe: "custom",
      startDate: start || null,
      endDate: end || null,
    };
  };
  const funnelStepsExportParams = new URLSearchParams({
    export_type: "funnel_steps",
    timeframe: exportTimeframeParam,
  });
  [variant, compareVariant].filter(Boolean).forEach((exportVariant) => {
    funnelStepsExportParams.append("variant_id[]", exportVariant.id);
  });
  if (exportTimeframeParam === "custom") {
    funnelStepsExportParams.set(
      "start_date",
      utcDayInputValue(rangeStartValue)
    );
    funnelStepsExportParams.set("end_date", utcDayInputValue(rangeEndValue));
  }
  const funnelStepsExportUrl = `/websites/${cta.website_id}/cta/${
    cta.id
  }.csv?${funnelStepsExportParams.toString()}`;

  useEffect(() => {
    setTimeframeType(resolveSelectedTimeframe());
    if (props.selectedRangeStart) {
      setRangeStartValue(props.selectedRangeStart);
    }
    if (props.selectedRangeEnd) {
      setRangeEndValue(props.selectedRangeEnd);
    }
  }, [
    props.selectedTimeframe,
    props.selectedRangeStart,
    props.selectedRangeEnd,
  ]);

  useEffect(() => {
    if (!variantId) return;
    document.dispatchEvent(
      new CustomEvent("funnel-breakdown:variant-changed", {
        detail: { variantId: Number(variantId) },
      })
    );
  }, [variantId]);

  useEffect(() => {
    const { timeframe, startDate, endDate } = resolveFunnelChatTimeframe();
    if (!timeframe) return;
    document.dispatchEvent(
      new CustomEvent("funnel-breakdown:timeframe-changed", {
        detail: { timeframe, startDate, endDate },
      })
    );
  }, [timeframeType, rangeStartValue, rangeEndValue]);

  useEffect(() => {
    const row = document.querySelector(".funnel-breakdown-chat-row");
    if (row) {
      row.classList.toggle("funnel-breakdown-chat-row--comparing", isComparing);
    }
    document.dispatchEvent(
      new CustomEvent("funnel-breakdown:compare-changed", {
        detail: {
          comparing: isComparing,
          compareVariantId: compareVariant ? Number(compareVariant.id) : null,
        },
      })
    );
    return () => {
      if (row) {
        row.classList.remove("funnel-breakdown-chat-row--comparing");
      }
    };
  }, [isComparing, compareVariant]);

  useEffect(() => {
    setCompareVariantId("");
  }, [cta.id]);

  useEffect(() => {
    if (gateReports) {
      $.getScript("https://fast.wistia.com/assets/external/E-v1.js");
      $.getScript("https://fast.wistia.com/embed/medias/s1hrpbbk2u.jsonp");
    }
  }, [props.cta.id]);

  const renderSteps = (reportVariant, keyPrefix, reportIdPrefix = "") =>
    reportVariant.steps.map((step) => (
      <StepReport
        key={`${keyPrefix}${step.id}`}
        cta={cta}
        step={step}
        timeframeType={timeframeType}
        range_start={range_start}
        range_end={range_end}
        firstStep={reportVariant.steps[0]}
        externalProductValues={props.externalProductValues}
        currency={props.currency}
        gateReports={gateReports}
        reportingGoals={props.reportingGoals}
        minWidth="750px"
        reportIdPrefix={reportIdPrefix}
      />
    ));

  return (
    <React.Fragment>
      <div
        className={`row hard-center smooth VariantReport${
          isComparing ? " VariantReport--comparing" : ""
        }`}
        style={{ minWidth: isComparing ? undefined : "750px" }}
      >
        <div className="row hard-center some-vertical-space">
          <div className="funnel-breakdown-toolbar">
            <h4 className="bold">Funnel Breakdown </h4>
            <div className="funnel-breakdown-toolbar-controls">
              <select
                className="text-field funnel-breakdown-toolbar-select"
                onChange={(event) => {
                  const nextVariantId = event.target.value;
                  setVariantId(nextVariantId);
                  if (String(compareVariantId) === String(nextVariantId)) {
                    setCompareVariantId("");
                  }
                  document.dispatchEvent(
                    new CustomEvent("funnel-breakdown:variant-changed", {
                      detail: { variantId: Number(nextVariantId) },
                    })
                  );
                }}
                value={variantId}
                aria-label="Funnel variant"
              >
                {selectableVariants.map((option) => (
                  <option key={`variant-option-${option.id}`} value={option.id}>
                    {variantLabel(option)}
                  </option>
                ))}
              </select>
              {compareOptions.length > 0 && (
                <select
                  className="text-field funnel-breakdown-toolbar-select funnel-breakdown-toolbar-select--compare"
                  onChange={(event) => {
                    setCompareVariantId(event.target.value);
                  }}
                  value={compareVariantId}
                  aria-label="Compare variant"
                >
                  <option value="">No Comparison</option>
                  {compareOptions.map((option) => (
                    <option
                      key={`compare-variant-option-${option.id}`}
                      value={option.id}
                    >
                      {variantLabel(option)}
                    </option>
                  ))}
                </select>
              )}
              {timeframeType == "Date Range" && (
                <React.Fragment>
                  <input
                    type="date"
                    aria-label="Range start"
                    className="text-field funnel-breakdown-toolbar-select"
                    value={utcDayInputValue(rangeStartValue)}
                    onChange={(event) => {
                      const value = utcDayStart(event.target.value);
                      if (!value) return;
                      setRangeStartValue(value);
                    }}
                  />
                  <input
                    type="date"
                    aria-label="Range end"
                    className="text-field funnel-breakdown-toolbar-select"
                    value={utcDayInputValue(rangeEndValue)}
                    onChange={(event) => {
                      const value = utcDayEnd(event.target.value);
                      if (!value) return;
                      setRangeEndValue(value);
                    }}
                  />
                </React.Fragment>
              )}
              <select
                className="text-field funnel-breakdown-toolbar-select"
                onChange={(event) => setTimeframeType(event.target.value)}
                name="timeframe"
                value={timeframeType}
              >
                {props.timeframeTypes.map((option) => (
                  <option
                    value={option.value}
                    key={`timeframe-option-${option.value}`}
                  >
                    {[
                      "Today",
                      "Yesterday",
                      "This Week",
                      "This Month",
                      "This Quarter",
                      "This Year",
                    ].includes(option.value)
                      ? `${option.value} (Timezone: UTC)`
                      : option.value == "Date Range"
                      ? "Date Range (UTC)"
                      : option.value}
                  </option>
                ))}
              </select>
              <a
                href={funnelStepsExportUrl}
                className="funnel-breakdown-toolbar-action"
              >
                <button
                  type="button"
                  className="btn toolbar-btn field-title !border-slate-200"
                  style={{ background: "#fff", height: "36px" }}
                >
                  <span
                    className="glyphicon glyphicon-save"
                    style={{ fontSize: "12px", marginRight: "5px" }}
                  ></span>
                  Export
                </button>
              </a>
              <a
                href={variantSubmissionsPath}
                className="funnel-breakdown-toolbar-action bg-white inline-flex items-center justify-center gap-3 border-solid border-[1px] py-3 px-5 rounded-md border-slate-200 text-black text-xl hover:bg-blue-100 hover:border-blue-100 hover:text-blue-600"
                style={{ textDecoration: "none", fontWeight: 500 }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="19"
                  viewBox="0 0 18 19"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M3.375 3.5C3.375 2.87868 3.87868 2.375 4.5 2.375H10.1893C10.4882 2.375 10.7749 2.49377 10.9862 2.70503L14.297 6.01578C14.5082 6.22703 14.627 6.51378 14.627 6.81075V15.5C14.627 16.1213 14.1483 16.625 13.527 16.625H4.5C3.87868 16.625 3.375 16.1213 3.375 15.5V3.5Z"
                    stroke="black"
                    strokeWidth="1.3"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6.75 10.25H11.25M6.75 13.25H9.75"
                    stroke="black"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                  />
                </svg>
                Submissions
              </a>
            </div>
          </div>
        </div>
        <div
          className="flex"
          style={isComparing ? { minWidth: 0, maxWidth: "100%" } : undefined}
        >
          <div
            className={`relative row hard-center steps-reports-container border-solid border-slate-100 border-0 border-t-[1px]${
              isComparing ? " funnel-breakdown-compare-columns" : ""
            }`}
            style={
              isComparing
                ? { width: "100%", minWidth: 0, overflowX: "auto" }
                : { width: "100%" }
            }
          >
            {isComparing ? (
              <React.Fragment>
                <div
                  className="funnel-breakdown-compare-column"
                  style={{ minWidth: 750, flex: "1 0 750px" }}
                >
                  <h4
                    style={{
                      margin: "12px 15px 8px",
                      paddingBottom: "8px",
                      borderBottom: "1px solid #E9EBf1",
                    }}
                  >
                    {variantLabel(variant)}
                  </h4>
                  {renderSteps(variant, "step-")}
                </div>
                <div
                  className="funnel-breakdown-compare-column"
                  style={{ minWidth: 750, flex: "1 0 750px" }}
                >
                  <h4
                    style={{
                      margin: "12px 15px 8px",
                      paddingBottom: "8px",
                      borderBottom: "1px solid #E9EBf1",
                    }}
                  >
                    {variantLabel(compareVariant)}
                  </h4>
                  {renderSteps(
                    compareVariant,
                    "compare-step-",
                    `v${compareVariant.id}-`
                  )}
                </div>
              </React.Fragment>
            ) : variant ? (
              renderSteps(variant, "step-")
            ) : null}

            {gateReports && (
              <div
                className="row absolute top-0 bottom-0 right-0 left-0 w-auto h-full p-5"
                style={{
                  backdropFilter: "blur(4px)",
                }}
              ></div>
            )}
          </div>
          {gateReports && (
            <div className="p-7 mt-10 mb-5 z-20">
              <div className="row hard-center bg-slate-100 rounded-lg p-7 !pb-0">
                <div className="row hard-center some-vertical-space">
                  <div
                    className="wistia_responsive_padding"
                    data-id="s1hrpbbk2u"
                    style={{ padding: "56.25% 0 0 0", position: "relative" }}
                  >
                    <div
                      className="wistia_responsive_wrapper"
                      style={{
                        height: "100%",
                        left: "0",
                        position: "absolute",
                        top: "0",
                        width: "100%",
                      }}
                    >
                      <div
                        className="wistia_embed wistia_async_s1hrpbbk2u popover=true videoFoam=true"
                        style={{
                          display: "inline-block",
                          height: "100%",
                          position: "relative",
                          width: "100%",
                        }}
                        ref={wistia}
                      >
                        <img
                          src="https://fast.wistia.com/embed/medias/s1hrpbbk2u/swatch"
                          style={{
                            filter: "blur(5px)",
                            height: "100%",
                            objectFit: "contain",
                            width: "100%",
                          }}
                          alt=""
                          aria-hidden="true"
                          onLoad={() => {
                            wistia.current.style.opacity = 1;
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row hard-center">
                  <p className="bold">Get funnel breakdowns</p>
                  <p>For each funnel step, element and answer, get insights:</p>
                </div>
                <div className="row">
                  <div className="col-xs-1 !pr-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                    >
                      <path
                        d="M3.5981 10.6577L8.62006 2.88562C9.02581 2.25767 10 2.54503 10 3.29266V7.23333C10 7.28856 10.0448 7.33333 10.1 7.33333H13.842C14.3285 7.33333 14.6128 7.8819 14.3322 8.27934L9.36273 15.3195C8.94073 15.9173 8 15.6187 8 14.887V11.6833C8 11.6281 7.95523 11.5833 7.9 11.5833H4.10205C3.62672 11.5833 3.34013 11.0569 3.5981 10.6577Z"
                        fill="#003FFF"
                      />
                    </svg>
                  </div>
                  <div className="col-xs-10 no-padding-right text-xl">
                    Drop off rates
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-1 !pr-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                    >
                      <path
                        d="M3.5981 10.6577L8.62006 2.88562C9.02581 2.25767 10 2.54503 10 3.29266V7.23333C10 7.28856 10.0448 7.33333 10.1 7.33333H13.842C14.3285 7.33333 14.6128 7.8819 14.3322 8.27934L9.36273 15.3195C8.94073 15.9173 8 15.6187 8 14.887V11.6833C8 11.6281 7.95523 11.5833 7.9 11.5833H4.10205C3.62672 11.5833 3.34013 11.0569 3.5981 10.6577Z"
                        fill="#003FFF"
                      />
                    </svg>
                  </div>
                  <div className="col-xs-10 no-padding-right text-xl">
                    Element reports
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-1 !pr-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                    >
                      <path
                        d="M3.5981 10.6577L8.62006 2.88562C9.02581 2.25767 10 2.54503 10 3.29266V7.23333C10 7.28856 10.0448 7.33333 10.1 7.33333H13.842C14.3285 7.33333 14.6128 7.8819 14.3322 8.27934L9.36273 15.3195C8.94073 15.9173 8 15.6187 8 14.887V11.6833C8 11.6281 7.95523 11.5833 7.9 11.5833H4.10205C3.62672 11.5833 3.34013 11.0569 3.5981 10.6577Z"
                        fill="#003FFF"
                      />
                    </svg>
                  </div>
                  <div className="col-xs-10 no-padding-right text-xl">
                    Answer statistics
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-1 !pr-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="15"
                      viewBox="0 0 15 15"
                      fill="none"
                    >
                      <path
                        d="M10.4861 4.72222C10.3479 4.33003 10.0955 3.98817 9.7613 3.74065C9.42715 3.49313 9.02657 3.35127 8.61111 3.33333H5.83333C5.2808 3.33333 4.75089 3.55283 4.36019 3.94353C3.96949 4.33423 3.75 4.86413 3.75 5.41667C3.75 5.9692 3.96949 6.49911 4.36019 6.88981C4.75089 7.28051 5.2808 7.5 5.83333 7.5H8.61111C9.16364 7.5 9.69355 7.71949 10.0842 8.11019C10.475 8.5009 10.6944 9.0308 10.6944 9.58333C10.6944 10.1359 10.475 10.6658 10.0842 11.0565C9.69355 11.4472 9.16364 11.6667 8.61111 11.6667H5.83333C5.41788 11.6487 5.0173 11.5069 4.68315 11.2593C4.34899 11.0118 4.09656 10.67 3.95833 10.2778M7.22222 1.25V3.33333M7.22222 11.6667V13.75"
                        stroke="#003FFF"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="col-xs-10 no-padding-right text-xl">
                    Revenue insights
                  </div>
                </div>
                <div className="row mb-5 mt-2">
                  <div className="col-xs-12">
                    <button
                      className="btn bg-black !text-white font-bold py-2 px-4"
                      onClick={(event) => {
                        subscriptions.upgrade_modal(
                          event,
                          false,
                          "get funnel breakdowns",
                          "pro",
                          "Triggered feature CTA – Full Breakdown",
                          false,
                          "s1hrpbbk2u"
                        );
                      }}
                    >
                      <svg
                        className="mr-3"
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M3.30039 4.40019V3.94305C3.30039 2.4236 4.50468 1.2002 6.00039 1.2002C7.49611 1.2002 8.70039 2.4236 8.70039 3.94305V4.40019M3.30039 4.40019C2.80539 4.40019 2.40039 4.81162 2.40039 5.31448V9.88591C2.40039 10.3888 2.80539 10.8002 3.30039 10.8002H8.70039C9.19539 10.8002 9.60039 10.3888 9.60039 9.88591V5.31448C9.60039 4.81162 9.19539 4.40019 8.70039 4.40019M3.30039 4.40019H8.70039"
                          stroke="white"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                        />
                      </svg>
                      Upgrade To Unlock
                    </button>
                    <p className="text-xl mt-5">
                      <a
                        href="https://support.convertflow.com/en/articles/2514470-conversion-reporting-funnel-breakdowns"
                        target="_blank"
                      >
                        Learn more →
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default VariantReport;
